import { splitDescriptionChunks } from '../description-chunks';
import { LINE_HEIGHT_PX, type PrintTemplateId } from '../page-template';
import { getCompanyLetterhead } from '../company-letterhead';
import { estimateWrappedLineCount } from '../description-chunks';
import type {
  PrintAddressColumn,
  PrintColumn,
  PrintDocumentModel,
  PrintLetterhead,
  PrintPartyBlock,
  PrintRow,
  PrintSection,
  PrintTermRow,
  PrintWatermark,
  RichPrintRenderPayload,
} from '../types';
import type { ApprovalPrintSigner } from '@/services/approval.service';

export type SimplePrintLine = {
  id?: string;
  lineNumber: number | string;
  description: string;
  descriptionChunks?: string[];
  cells?: Record<string, string>;
  extraLines?: string[];
  kind?: string;
};

export type BuildSimplePrintDocumentInput = {
  docType: string;
  title: string;
  subtitle?: string;
  documentNumber: string;
  partyName: string;
  templateId?: PrintTemplateId;
  letterhead?: PrintLetterhead | false;
  watermark?: PrintWatermark;
  continuationLabel?: string;
  /** Estimated first-page chrome height (meta/header/intro). */
  firstPageChromeHeightPx?: number;
  metaLines?: Array<{ label: string; value: string }>;
  introText?: string;
  /** Rich letter party block (M/S + document refs). */
  partyBlock?: PrintPartyBlock;
  introGreeting?: string;
  introParagraphs?: string[];
  addressGrid?: PrintAddressColumn[];
  columns: PrintColumn[];
  lines: SimplePrintLine[];
  tableTitle?: string;
  /** Final blocks with estimated heights. */
  finals?: Array<{ id: string; heightPx: number; keepTogetherWithNext?: boolean }>;
  termsHeading?: string;
  termRows?: PrintTermRow[];
  termsExtra?: string[];
  termsHeightPx?: number;
  signers?: ApprovalPrintSigner[];
  signersHeightPx?: number;
  renderPayload?: RichPrintRenderPayload | Record<string, unknown>;
};

function textH(text: string, cpl = 80): number {
  return estimateWrappedLineCount(text, cpl) * LINE_HEIGHT_PX;
}

function partyBlockHeight(block?: PrintPartyBlock): number {
  if (!block) return 0;
  const rows = Math.max(block.leftRows.length, block.rightRows.length, 1);
  return rows * LINE_HEIGHT_PX + 20;
}

function addressGridHeight(cols?: PrintAddressColumn[]): number {
  if (!cols?.length) return 0;
  const lines = Math.max(...cols.map((c) => 1 + (c.name ? 1 : 0) + c.lines.length), 2);
  return lines * LINE_HEIGHT_PX + 24;
}

function introRichHeight(greeting?: string, paragraphs?: string[], introText?: string): number {
  let h = 0;
  if (greeting) h += LINE_HEIGHT_PX + 4;
  for (const p of paragraphs || []) h += textH(p, 90) + 8;
  if (!h && introText) h = textH(introText, 90) + 16;
  return h;
}

function termsHeight(
  heading?: string,
  rows?: PrintTermRow[],
  extra?: string[],
  override?: number
): number {
  if (override != null) return override;
  if (!heading && !rows?.length && !extra?.length) return 0;
  let h = heading ? LINE_HEIGHT_PX + 8 : 0;
  h += (rows?.length || 0) * (LINE_HEIGHT_PX - 1);
  for (const e of extra || []) h += textH(e, 90) + 6;
  return Math.max(h, 40);
}

/** Approvals render in a horizontal grid (≈3 columns), not one stacked row per signer. */
export function estimateApprovalSignersHeight(signerCount: number): number {
  if (signerCount <= 0) return 0;
  const colsPerRow = 3;
  const rows = Math.ceil(signerCount / colsPerRow);
  return 36 + rows * 72;
}

/**
 * Fast path for migrating existing prints onto the shared engine.
 * Supports rich letter blocks (party, intro, Bill/Ship, terms, signers).
 */
export function buildSimplePrintDocument(input: BuildSimplePrintDocumentInput): PrintDocumentModel {
  const templateId = input.templateId ?? 'a4-portrait';
  const letterhead =
    input.letterhead === false
      ? undefined
      : input.letterhead ??
        (templateId === 'letter-letterhead' ? getCompanyLetterhead() : undefined);

  const headerH = 28 + (input.subtitle ? 18 : 0) + 8;
  const partyH = partyBlockHeight(input.partyBlock);
  const metaH =
    !input.partyBlock && input.metaLines
      ? Math.max(4, input.metaLines.length) * LINE_HEIGHT_PX + 12
      : 0;
  const introH = introRichHeight(input.introGreeting, input.introParagraphs, input.introText);
  const addressH = addressGridHeight(input.addressGrid);
  const chrome =
    input.firstPageChromeHeightPx ?? headerH + partyH + metaH + introH + addressH;

  const firstPageSections: PrintSection[] = [
    { id: 'documentHeader', heightPx: headerH },
  ];
  if (partyH > 0) firstPageSections.push({ id: 'partyBlock', heightPx: partyH });
  else if (metaH > 0) firstPageSections.push({ id: 'customerBlock', heightPx: metaH });
  if (introH > 0) firstPageSections.push({ id: 'intro', heightPx: introH });
  if (addressH > 0) firstPageSections.push({ id: 'addressGrid', heightPx: addressH });

  if (input.firstPageChromeHeightPx && chrome > headerH + partyH + metaH + introH + addressH) {
    firstPageSections.push({
      id: 'extraChrome',
      heightPx: chrome - headerH - partyH - metaH - introH - addressH,
    });
  }

  const rows: PrintRow[] = input.lines.map((line, idx) => ({
    id: line.id ?? `row-${idx}`,
    lineNumber: line.lineNumber,
    description: line.description,
    descriptionChunks: line.descriptionChunks?.length
      ? line.descriptionChunks
      : splitDescriptionChunks(line.description),
    extraLines: line.extraLines,
    cells: line.cells ?? {},
    kind: line.kind,
  }));

  const finalSections: PrintSection[] = (input.finals ?? []).map((f) => ({
    id: f.id,
    heightPx: f.heightPx,
    keepTogether: true,
    keepTogetherWithNext: f.keepTogetherWithNext,
  }));

  const tH = termsHeight(input.termsHeading, input.termRows, input.termsExtra, input.termsHeightPx);
  if (tH > 0) {
    finalSections.push({ id: 'terms', heightPx: tH, keepTogether: true });
  }
  if (input.signers?.length) {
    finalSections.push({
      id: 'signers',
      heightPx:
        input.signersHeightPx ?? estimateApprovalSignersHeight(input.signers.length),
      keepTogether: true,
    });
  }

  const renderPayload: RichPrintRenderPayload = {
    ...(input.renderPayload as RichPrintRenderPayload | undefined),
    metaLines: input.metaLines ?? (input.renderPayload as RichPrintRenderPayload | undefined)?.metaLines,
    introText: input.introText ?? (input.renderPayload as RichPrintRenderPayload | undefined)?.introText,
    partyBlock: input.partyBlock,
    introGreeting: input.introGreeting,
    introParagraphs: input.introParagraphs,
    addressGrid: input.addressGrid,
    termsHeading: input.termsHeading,
    termRows: input.termRows,
    termsExtra: input.termsExtra,
    signers: input.signers,
    finalBlocks:
      (input.renderPayload as RichPrintRenderPayload | undefined)?.finalBlocks ??
      undefined,
  };

  return {
    docType: input.docType,
    title: input.title,
    subtitle: input.subtitle,
    documentNumber: input.documentNumber,
    partyName: input.partyName,
    templateId,
    letterhead,
    watermark: input.watermark,
    continuationLabel:
      input.continuationLabel ?? `${input.documentNumber} | ${input.partyName}`,
    firstPageSections,
    tables: [
      {
        id: 'main',
        title: input.tableTitle,
        columns: input.columns,
        rows,
      },
    ],
    finalSections,
    renderPayload,
  };
}
